import React, { useEffect, useState } from "react";
import axios from "axios";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { API_ENDPOINT } from "../../../../services/api/resources";
import classes from "./charts.module.css";
import { useSelector } from "react-redux";

ChartJS.register(
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

const InvoiceChart = () => {
  const Data = useSelector((state) => state.loginData.value);
  // const org_token = Data?.organization_token;
  const user_token = Data?.user?.token || Data?.user_token;

  const [dataSets, setDataSets] = useState({
    daily: [],
    weekly: [],
    monthly: [],
    yearly: [],
  });
  const [filter, setFilter] = useState("daily");

  useEffect(() => {
    axios
      .post(
        API_ENDPOINT.invoice_summary,
        { org_token: user_token },

        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
      .then((res) => {
        const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];

        const dailyRaw = res.data.daily.map((item) => {
          const date = new Date(item.day);
          const label = `${date.getDate()} ${months[date.getMonth()]}`;
          return { x: label, y: item.count };
        });

        const weeklyRaw = res.data.weekly.map((item) => {
          const date = new Date(item.day);
          return { x: weekdays[date.getDay()], y: item.count };
        });

        // Ensure all days are represented
        const sortedWeekly = weekdays.map((day) => {
          const found = weeklyRaw.find((d) => d.x === day);
          return found || { x: day, y: 0 };
        });

        const monthlyRaw = res.data.monthly.map((item) => {
          const date = new Date(item.month);
          return {
            monthIndex: date.getMonth(),
            count: item.count,
          };
        });

        const filledMonthly = months.map((label, index) => {
          const found = monthlyRaw.find((m) => m.monthIndex === index);
          return { x: label, y: found ? found.count : 0 };
        });

        // Yearly
        const yearly = res.data.yearly.map((item) => ({
          x: item.year.slice(0, 4),
          y: item.count,
        }));

        setDataSets({
          daily: dailyRaw,
          weekly: sortedWeekly,
          monthly: filledMonthly,
          yearly,
        });
      });
  }, []);

  const chartData = {
    labels: dataSets[filter].map((d) => d.x),
    datasets: [
      {
        label: `Invoices (${filter})`,
        data: dataSets[filter].map((d) => d.y),
        backgroundColor: "rgba(54, 162, 235, 0.5)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
        tension: 0.3,
        fill: true,
      },
    ],
  };

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: "circle",
          color: "#6b7280",
          font: {
            weight: "600",
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.85)",
        padding: 12,
        usePointStyle: true,
        bodyFont: {
          weight: "500",
        },
        titleFont: {
          weight: "400",
        },
        callbacks: {
          label: (context) => {
            return ` ${context.dataset.label}: ${
              context.raw.y?.[1] || context.raw
            }`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: {
          color: "#6b7280",
          font: { size: 10, weight: "500" },
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 10,
          color: "#6b7280",
          font: {
            size: 12,
            weight: "500",
          },
        },
        grid: {
          display: true,
          drawBorder: false,
          color: "#e5e7eb",
          lineWidth: 1,
        },
      },
    },
  };

  // Gradient Area Chart Data
  const gradientLineChartData = {
    labels: dataSets[filter].map((d) => d.x),
    datasets: [
      {
        label: `Invoices(${filter})`,
        data: dataSets[filter].map((d) => d.y),
        borderColor: "#3b82f6",
        borderWidth: 3,
        pointBackgroundColor: "#fff",
        pointBorderColor: "#3b82f6",
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        tension: 0.4,
        fill: true,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(59, 130, 246, 0.5)");
          gradient.addColorStop(1, "rgba(59, 130, 246, 0)");
          return gradient;
        },
      },
    ],
  };

  const ChartComponent = () => {
    return <Line data={gradientLineChartData} options={baseOptions} />;
  };

  return (
    <div className={classes.invoiceChartWrapper}>
      <div className={classes.chartHeader}>
        <h2 className={classes.chartTitle}>Invoice Trends</h2>
        <div className={classes.filterControl}>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className={classes.filterSelect}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>

      <div className={classes.chartContainer}>
        <ChartComponent />
      </div>
    </div>
  );
};

export default InvoiceChart;
